import { useState } from "react";
import { Check, CreditCard, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Subscription = () => {
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // "monthly" or "annual"

  const plans = [
    {
      id: "basic",
      name: "Basic",
      description: "Piano base per iniziare",
      monthlyPrePrice: 10,
      monthlyPrice: 10,
      annualPrePrice: 100,
      annualPrice: 100,
      features: [
        "piano base 1",
        "piano base 2",
        "piano base 3"
      ],
      recommended: false
    },
    {
      id: "pro",
      name: "Pro",
      description: "Il piano più popolare per professionisti",
      monthlyPrice: 20,
      monthlyPrePrice: 25,
      annualPrePrice: 250,
      annualPrice: 200,
      features: [
        "piano PRO 1",
        "piano PRO 2",
        "piano PRO 3"
      ],
      recommended: true
    },
    {
      id: "enterprise",
      name: "Enterprise",
      description: "Per aziende con esigenze avanzate",
      monthlyPrice: 25,
      monthlyPrePrice: 30,  
      annualPrePrice: 300,
      annualPrice: 250,
      features: [
        "piano Enterprise 1",
        "piano Enterprise 2",
        "piano Enterprise 3"
      ],
      recommended: false
    }
  ];

  const calculateSavings = (plan) => {
    const yearlyFromMonthly = plan.monthlyPrice * 12;
    return yearlyFromMonthly - plan.annualPrice;
  };

  const handleSubscribe = (plan) => {
    const price = billingPeriod === "monthly" ? plan.monthlyPrice : plan.annualPrice;
    const period = billingPeriod === "monthly" ? "mensile" : "annuale";
    
    // TODO: Integrazione Stripe - placeholder per ora
    toast.info(`Apertura checkout per piano ${plan.name} - ${price}€ ${period}`, {
      description: "Integrazione Stripe da configurare"
    });
    
    // Esempio di come aprire Stripe Checkout (da implementare con chiavi reali):
    // const stripe = await loadStripe('pk_test_...');
    // stripe.redirectToCheckout({ sessionId: 'session_id_from_backend' });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-2">
          <CreditCard className="text-primary" size={32} />
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Area Abbonamenti
          </h1>
        </div>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Scegli il piano perfetto per le tue esigenze e inizia a gestire i tuoi pneumatici in modo professionale
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4 p-4 bg-card border border-border rounded-lg max-w-sm mx-auto">
        <Label 
          htmlFor="billing-toggle" 
          className={`font-semibold ${billingPeriod === "monthly" ? "text-foreground" : "text-muted-foreground"}`}
        >
          Mensile
        </Label>
        <Switch
          id="billing-toggle"
          checked={billingPeriod === "annual"}
          onCheckedChange={(checked) => setBillingPeriod(checked ? "annual" : "monthly")}
          className="data-[state=checked]:bg-primary"
        />
        <Label 
          htmlFor="billing-toggle" 
          className={`font-semibold ${billingPeriod === "annual" ? "text-foreground" : "text-muted-foreground"}`}
        >
          Annuale
        </Label>
        {billingPeriod === "annual" && (
          <Badge variant="default" className="bg-primary text-primary-foreground">
            Risparmia fino al 20%
          </Badge>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {plans.map((plan) => {
          const price = billingPeriod === "monthly" ? plan.monthlyPrice : plan.annualPrice;
          const prePrice = billingPeriod === "monthly" ? plan.monthlyPrePrice : plan.annualPrePrice;
          const showPrePrice = prePrice !== price;
          const savings = calculateSavings(plan);
          const isRecommended = plan.recommended;

          return (
            <Card
              key={plan.id}
              className={`relative transition-all ${
                isRecommended
                  ? "border-primary shadow-lg shadow-primary/20 md:scale-105"
                  : "border-border hover:border-primary/50"
              }`}
            >
              {isRecommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground shadow-lg">
                    <Sparkles size={14} className="mr-1" />
                    Consigliato
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Price */}
                <div className="text-center">
                  <div className="flex items-baseline justify-center gap-2">
                    {showPrePrice && (
                      <span className="text-2xl text-muted-foreground line-through">
                        {prePrice}€
                      </span>
                    )}
                    <span className="text-4xl font-bold">{price}€</span>
                    <span className="text-muted-foreground">
                      /{billingPeriod === "monthly" ? "mese" : "anno"}
                    </span>
                  </div>
                  {billingPeriod === "annual" && (
                    <Badge variant="outline" className="mt-2 text-primary border-primary">
                      Risparmi {savings}€ all'anno
                    </Badge>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check size={20} className="text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  onClick={() => handleSubscribe(plan)}
                  className={`w-full h-12 font-semibold ${
                    isRecommended
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  }`}
                >
                  Scegli {plan.name}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="text-center text-sm text-muted-foreground max-w-2xl mx-auto">
        <p>
          Tutti i piani includono aggiornamenti gratuiti e supporto tecnico.
          Puoi annullare o modificare il tuo piano in qualsiasi momento.
        </p>
      </div>
    </div>
  );
};

export default Subscription;
